import { AppDialog } from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Building2, Globe2, ListChecks } from "lucide-react";
import { useTran } from "@/hooks";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";

const ViewMasterDataDialog = ({ open, onOpenChange, row }) => {
  const { t } = useTran();
  const organizations = Array.isArray(row?.organizations)
    ? row.organizations
    : [];
  const organizationObjects = organizations.filter(
    (org) => typeof org === "object" && (org?.name || org?.label),
  );
  const organizationNames = organizations
    .map((organization) => {
      if (typeof organization === "string") return organization;
      return organization?.name || organization?.label || "";
    })
    .filter(Boolean);
  const isAllOrganization = Boolean(row?.isAllOrganization);

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("View Metric Assignment")}
      description={t("Review disclosure and organization scope details.")}
      size="lg"
      footer={
        <div className="flex w-full justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("Close")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 mx-2">
        {/* Disclosure Information */}
        <div className="rounded-xl border border-border bg-muted/30 p-5">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("Disclosure Information")}
            </p>

            {row?.disclosureCategory && (
              <Badge variant="outline" className="bg-background">
                {row.disclosureCategory}
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Code */}
            <div>
              <p className="text-xs text-muted-foreground">{t("Code")}</p>
              <p className="mt-1 text-sm font-semibold text-foreground break-all">
                {row?.disclosureCode || "-"}
              </p>
            </div>

            {/* Framework */}
            <div>
              <p className="text-xs text-muted-foreground">{t("Framework")}</p>
              <p className="mt-1 text-sm font-semibold text-foreground break-all">
                {row?.framework || "-"}
              </p>
            </div>

            {/* Year */}
            <div>
              <p className="text-xs text-muted-foreground">{t("Year")}</p>
              <p className="mt-1 text-sm font-semibold text-foreground">
                {row?.reportingYear || row?.year || "-"}
              </p>
            </div>

            {/* Title */}
            <div>
              <p className="text-xs text-muted-foreground">{t("Title")}</p>
              <p className="mt-1 text-sm font-semibold break-words whitespace-pre-wrap">
                {row?.disclosureTitle || "-"}
              </p>
            </div>
          </div>
        </div>

        {/* Organization Scope */}
        <div className="rounded-xl border border-border p-5">
          <div className="mb-3 flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm font-semibold text-foreground">
              {t("Organization Scope")}
            </p>
          </div>

          <div className="flex items-start gap-3">
            <Badge variant={isAllOrganization ? "default" : "secondary"}>
              {isAllOrganization ? t("All Organizations") : t("Specific")}
            </Badge>

            <p className="text-xs text-muted-foreground leading-relaxed">
              {isAllOrganization
                ? t(
                    "This disclosure applies to all organizations in the company.",
                  )
                : t("This disclosure applies only to selected organizations.")}
            </p>
          </div>
        </div>

        {/* Organizations */}
        <div className="rounded-xl border border-border p-5">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm font-semibold text-foreground">
                {t("Organizations")}
              </p>
            </div>

            {!isAllOrganization && organizationNames.length > 0 && (
              <Badge variant="outline">
                {organizationNames.length} {t("selected")}
              </Badge>
            )}
          </div>

          {isAllOrganization ? (
            <div className="flex items-center gap-2 rounded-md border border-dashed border-border bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
              <Globe2 className="h-4 w-4" />
              {t("All organizations included")}
            </div>
          ) : organizationNames.length === 0 ? (
            <div className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
              {t("No organizations selected")}
            </div>
          ) : (
            <div className="max-h-52 overflow-y-auto rounded-md border border-border bg-muted/10 p-3">
              <div className="flex flex-wrap gap-2">
                {(organizationObjects.length > 0
                  ? organizationObjects
                  : organizationNames.map((name) => ({ name }))
                ).map((org, idx) => (
                  <Badge
                    key={org.name || org.label || idx}
                    variant="secondary"
                    className="px-2 py-1 flex items-center gap-1"
                  >
                    {org.name || org.label}
                    {typeof org === "object" && (
                      <OrgStatusTag
                        status={org.status}
                        inactiveDate={org.inactiveDate}
                        className="text-[10px]"
                      />
                    )}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppDialog>
  );
};

export default ViewMasterDataDialog;
